/**
 * useRole — Derive the user's workspace role from auth context.
 *
 * Role data comes from AuthOrg.role (landed in S24).
 * If the role field is missing or unrecognized, we fall back to
 * least-privilege (non-admin) — we NEVER assume admin access.
 */

import { useAuth } from '@/lib/auth';

/** Recognized workspace roles */
export type WorkspaceRole = 'owner' | 'admin' | 'member';

/**
 * Returns the user's workspace role, defaulting to 'member' (least-privilege).
 * If the role field is missing or unrecognized, fallback is ALWAYS non-admin.
 */
export function useRole(): WorkspaceRole {
  const { org } = useAuth();
  const rawRole = org?.role;

  if (rawRole === 'owner') return 'owner';
  if (rawRole === 'admin') return 'admin';

  // Least-privilege fallback: any undefined/unrecognized role → member
  if (rawRole && rawRole !== 'member') {
    console.warn(`[useRole] Unrecognized role "${rawRole}", falling back to least-privilege (member)`);
  }

  return 'member';
}

/** Convenience: true only when role is strictly 'admin' or 'owner'. */
export function useIsAdmin(): boolean {
  const role = useRole();
  return role === 'admin' || role === 'owner';
}
